<template>
  <div
    v-on-clickaway="away"
    class="clearfix"
    data-cy="token-select-popover"
  >
    <input
      ref="searchInput"
      v-model="debouncedSearchText"
      type="search"
      class="form-control p-r-30"
      placeholder="Search Token"
      @keydown.esc="$emit('blur')"
    >
    <div class="group-by-dropdown">
      <!-- Tag Categories list - START -->

      <div
        v-for="(tagCategory, categoryId) in showMacros"
        :key="categoryId"
        data-cy="token-category-title"
      >
        <div
          class="group-by-category"
          data-cy="token-category-title"
          @click="toggleMacrosForCategory(categoryId)"
        >
          <span class="text-capitalize group-by-category__main">
            {{ tagCategory.name | camelCaseFormat }}
          </span>

          <i
            class="fa"
            :class="{
              'fa-angle-down': !showMacrosDropdown[categoryId],
              'fa-angle-up': showMacrosDropdown[categoryId],
            }"
          />
        </div>

        <div
          v-if="showMacrosDropdown[categoryId]"
          data-cy="token-category-macros"
        >
          <div
            v-for="tag in tagCategory.macros"
            :key="tag.id"
            class="p-l-15 clickable group-by-tag__name"
            @click="emitTag(categoryId, tag)"
          >
            <span
              class="text-capitalize"
              :class="{'group-by-tag__name--not-available': !tagAvailable(tag.populatedOn)}"
            >
              {{ tag.name | camelCaseFormat }} <i
                v-if="!tagAvailable(tag.populatedOn)"
                :title="$t('TOKEN_NOT_AVAILABLE_TT')"
                class="fa fa-ban m-l-5"
                aria-hidden="true"
              />
            </span>
          </div>
        </div>
      </div>

      <div
        v-if="noMacros"
        class="p-l-5 p-b-5 p-t-5 f-s-13 text-muted"
      >
        {{ emptyMacrosMessage }}
      </div>
      <!-- Tag Categories list - END -->
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mixin as clickaway } from 'vue-clickaway';
import { debounce, isEmpty, cloneDeep } from 'lodash';

import sharedMessages from '@/core/locales';

export default {
  name: 'MacroSelectionPopover',
  mixins: [clickaway],
  props: {
    macros: {
      type: Object,
      default() {},
    },
    focusDropdown: {
      type: Boolean,
      default: false,
    },
    triggerElement: {
      type: undefined,
      default() {
        return undefined;
      }
    },
    triggerEvent: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      showMacros: this.macros ? cloneDeep(this.macros) : {},
      searchText: '',
      showMacrosDropdown: {}
    };
  },
  computed: {
    debouncedSearchText: {
      get() {
        return this.searchText;
      },
      set: debounce(function setNewGroupBySearchValue(value) {
        this.searchText = value;
        // Reset the tag types shown mapping
        this.showMacros = {... this.macros};
        if (this.searchText) {
          const normalizedSearch = this.searchText.trim().toLowerCase().replace(/\s/g, '');
          this.showMacros = Object.entries(this.showMacros).reduce(
            (newObj, [macroTypeName, macroTypeValue]) => {
              const macroTypeValueClone = {...macroTypeValue};
              const normalizedTagGroupName = macroTypeName.toLowerCase().replace(/\s/g, '');

              // If the Type name includes the search param then we show the whole category without filtering, if not we filter for macros that match the criteria
              if (
                !macroTypeName.includes(normalizedSearch) 
              ) {
                const filteredMacrosFromType = this.getMacrosMatchingCriteria(macroTypeValueClone.macros, normalizedSearch);
                macroTypeValueClone.macros = filteredMacrosFromType;
              }

              // If the Group name matches or has at least 1 macro that matches the criteria we add it to the show group
              if (normalizedTagGroupName.includes(normalizedSearch) || Object.keys(macroTypeValueClone.macros).length > 0) {
                newObj[macroTypeName] = macroTypeValueClone;
              }
              return newObj;
            },
            {}
          );
        }
      }, 800),
    },
    noMacrosFound() {
      return this.searchText && isEmpty(this.showMacros);
    },
    noMacros() {
      return isEmpty(this.showMacros);
    },
    emptyMacrosMessage() {
      return 'No Tokens';
    },
  },
  watch: {
    macros(newValue) {
      this.showMacros = cloneDeep(newValue);
    },
    focusDropdown(newValue) {
      if (newValue) {
        this.$refs['searchInput'].focus();
      }
    },
  },
  methods: {
    away(event) {
      if (this.triggerElement) {
        const path =
          event.path || (event.composedPath ? event.composedPath() : undefined);
        if (path ? path.indexOf(this.triggerElement) < 0 : false) {
          this.$emit('blur', this.index);
        }
      }
    },
    getMacrosMatchingCriteria(macros, searchParam) {
      // Returns a list of Macros that match the search param
      return Object.entries(macros).reduce((newMacrosObj, [macroName, macroValue]) => {
        const normalizedMacroName = macroName.toLowerCase().replace(/\s/g, '');
        if (normalizedMacroName.includes(searchParam)) {
          newMacrosObj[macroName] = macroValue;
        }
        return newMacrosObj;
      }, {});
    },
    toggleMacrosForCategory(tagCategory) {
      Vue.set(this.showMacrosDropdown, tagCategory, !this.showMacrosDropdown[tagCategory]);
    },
    hideMacrosForCategory() {
      this.showMacrosDropdown = {};
    },
    emitTag(selectedTagCategory, tag) {
      this.$emit('selected-tag', { 
        field: `tag:${selectedTagCategory}:${tag.id}`, displayName: `${selectedTagCategory}:${tag.id}`, 
        category: selectedTagCategory,
        originalTag: tag,
      });
    },
    tagAvailable(triggerEvents) {
      if (!this.triggerEvent || !triggerEvents || (triggerEvents && triggerEvents.length === 0)) return true;
      const normalizedEvents = triggerEvents.map(name => name.toLowerCase());
      return normalizedEvents.includes(this.triggerEvent);
    }
  },
  i18n: {
    sharedMessages,
    locale: 'en',
  },
};
</script>
